import { useState } from "react";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import useSetQueryParams from "routes/club/hooks/useSetQueryParams";
import { useGetTracking } from "routes/club/hooks/useGetTracking";
import { get } from "lodash";
import { ClubType } from "routes/club/types";
import { Modal } from "components/modal";
import Pagination from "components/pagination";
import { generateUUID } from "utills/helpers";

interface PropType {
  open: boolean;
  targetClub: ClubType;
  handleClose: () => void;
}

const LogHistoryModal = (props: PropType) => {
  const { open, targetClub, handleClose } = props;

  const PAGE_SIZE = 5;

  const [searchParams, setSearchParams] = useSearchParams();
  const setQueryParams = useSetQueryParams();

  const paramsObject = Object.fromEntries(searchParams.entries());

  // const page = searchParams.get("log_history_page")
  //   ? Number(searchParams.get("log_history_page"))
  //   : 1;

  const { data, loading, paging, params, setParams } = useGetTracking(
    targetClub?.id ?? "",
    {
      page: 1,
      limit: PAGE_SIZE,
    }
  );

  const [filter, setFilter] = useState<{ page: number }>({ page: 1 });

  const handlePageChange = (value: number) => {
    setFilter({ ...filter, page: value });

    // Update URL search params
    // setSearchParams({
    //   ...paramsObject,
    //   log_history_page: value.toString(),
    // });

    // Update API search params
    setParams({
      ...params,
      page: value,
    });
  };

  const handleFormatMessage = (value: string) => {
    const formatted = value.split(" ")[0];

    return formatted;
  };

  const getValidKey = (key: string) => {
    if (
      key === "label_vi" ||
      key === "label_en" ||
      key === "key" ||
      key === "is_active"
    )
      return true;

    return false;
  };

  const handleFormatKey = (value: string) => {
    if (value === "label_vi") return "Name Vi";
    if (value === "label_en") return "Name En";
    if (value === "key") return "Key";
    if (value === "is_active") return "Status";

    return value;
  };

  const handleFormatHistoryValue = (value: string, key: string) => {
    if (value === "null") return "Empty";
    if (value === "true") return "Active";
    if (value === "false") return "Inactive";
    if (key === "created_at" || key === "updated_at")
      return dayjs(value).format("DD/MM/YYYY HH:mm:ss");

    return value;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Action History"
      closeOnClickBackdrop={false}
    >
      <div className="w-full p-4 flex flex-col gap-5">
        {!loading && data?.length === 0 ? (
          <span className="mt-5 text-center text-gray-600">
            No history found
          </span>
        ) : (
          <div className="w-full max-h-[300px] overflow-y-auto mb-3 pr-2 flex flex-col gap-5">
            {data?.map((item) => {
              const time = dayjs(get(item, "created_at", "")).format(
                "DD/MM/YYYY HH:mm:ss"
              );

              const dataObj = get(item, "data", {}) as Record<
                string,
                { from: any; to: any }
              >;

              const actions = Object.entries(dataObj).map(([key, value]) => ({
                key,
                from: String(value.from),
                to: String(value.to),
              }));

              return (
                <div
                  key={generateUUID()}
                  className="w-full flex flex-col gap-3 pb-3 border-b border-gray-200"
                >
                  <div className="flex flex-wrap items-center gap-5">
                    {/* User Email */}
                    <span>{get(item, "username", "")}</span>

                    {/* Action */}
                    <span className="text-[#1890FF]">
                      {handleFormatMessage(get(item, "message", ""))}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-start gap-5">
                    {/* Time */}
                    <span>{time}</span>

                    {/* Action */}
                    <div className="flex flex-col gap-5">
                      {actions?.map((action) => {
                        const isValidKey = getValidKey(action?.key);

                        if (!isValidKey) return null;

                        return (
                          <div
                            key={generateUUID()}
                            className="flex items-center gap-2"
                          >
                            <span className="font-bold">
                              {handleFormatKey(action?.key)}
                            </span>

                            <span>
                              {handleFormatHistoryValue(
                                action?.from,
                                action?.key
                              )}
                            </span>

                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="10"
                              viewBox="0 0 12 10"
                              fill="none"
                            >
                              <path
                                d="M10.3554 4.72986L5.49554 1.07026C5.45823 1.04236 5.41064 1.02673 5.36047 1.02673H4.22204C4.12685 1.02673 4.08311 1.12941 4.15515 1.18298L8.65998 4.57584H1.13221C1.07561 4.57584 1.0293 4.61602 1.0293 4.66513V5.33477C1.0293 5.38388 1.07561 5.42405 1.13221 5.42405H8.65869L4.15386 8.81691C4.08183 8.8716 4.12556 8.97316 4.22075 8.97316H5.39777C5.42221 8.97316 5.44665 8.96535 5.46466 8.95084L10.3554 5.27004C10.3999 5.23644 10.4356 5.19494 10.4601 5.14835C10.4846 5.10175 10.4972 5.05115 10.4972 4.99995C10.4972 4.94875 10.4846 4.89814 10.4601 4.85155C10.4356 4.80495 10.3999 4.76346 10.3554 4.72986Z"
                                fill="#19141E"
                              />
                            </svg>

                            <span>
                              {handleFormatHistoryValue(
                                action?.to,
                                action?.key
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Pagination
          currentPage={get(paging, "current_page", 1)}
          totalPage={get(paging, "total_page", 0)}
          total={{
            totalItems: get(paging, "total_item", 0),
            total: get(paging, "total", 0),
          }}
          onChange={handlePageChange}
        />
      </div>
    </Modal>
  );
};

export default LogHistoryModal;
